import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faArrowLeft,
  faHeart,
  faTrash,
  faPlus,
  faMinus,
  faShoppingCart,
  faStore,
  faMapMarkerAlt,
  faShoppingBag,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart"; 

const sampleSavedItems = [
  {
    _id: "P1001",
    name: "Handmade Cotton Bag",
    store: "Priya Creations",
    entrepreneur: "Priya Sharma",
    city: "Mumbai",
    price: 350,
    quantity: 2,
  },
  {
    _id: "P1002",
    name: "Organic Soap Pack",
    store: "Nature Bliss",
    entrepreneur: "Anjali Verma",
    city: "Pune",
    price: 199,
    quantity: 1,
  },
  {
    _id: "P1003",
    name: "Bamboo Water Bottle",
    store: "Eco Living",
    entrepreneur: "Neha Gupta",
    city: "Nashik",
    price: 240,
    quantity: 3,
  },
];

const HerKartSaved = () => {
  const [items, setItems] = useState(sampleSavedItems);
  const { fetchSaved, syncSaved } = useHerKart();
  const [isLive, setIsLive] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);

  const loadMySaved = async () => {
    setLoadingSaved(true);
    try {
      const response = await fetchSaved();
      if (response?.success) {
        setItems(response.items || []);
        setIsLive(true);
      }
    } catch (e) {
      console.error("Saved fetch error:", e);
    } finally {
      setLoadingSaved(false);
    }
  };

  useEffect(() => {
    if (isLive) syncSaved(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const totalItems = useMemo(() => {
    return items.reduce((sum, item) => sum + item.quantity, 0);
  }, [items]);

  const totalValue = useMemo(() => {
    return items.reduce((sum, item) => sum + item.quantity * item.price, 0);
  }, [items]);

  const increaseQty = (id) => {
    setItems((prev) =>
      prev.map((item) =>
        item._id === id
          ? {
              ...item,
              quantity: item.quantity + 1,
            }
          : item,
      ),
    );
  };

  const decreaseQty = (id) => {
    setItems((prev) =>
      prev.map((item) =>
        item._id === id
          ? {
              ...item,
              quantity: Math.max(1, item.quantity - 1),
            }
          : item,
      ),
    );
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((item) => item._id !== id));
  };
  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex flex-col max-w-7xl px-4 py-2">
          <div className="flex items-center gap-3">
            <Link
              to="/herkart/products"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>

            <div>
              <h1 className="text-xl text-cyan-200 font-bold">My Saved Items</h1>

              <p className="text-xs text-slate-400">
                Buy them whenever you're ready
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadMySaved}
              className="rounded-xl bg-white/10 w-fit px-2 py-1 text-xs hover:bg-white/20"
            >
              {loadingSaved ? "Loading..." : isLive ? "🔄 Synced" : "🔄 Load My Saved"}
            </button>

            <div className="rounded-xl bg-pink-600/20 px-4 py-2 text-pink-300">
              <FontAwesomeIcon icon={faHeart} className="mr-2" />
              {totalItems} Item{totalItems > 1 ? "s" : ""}
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-6">
        {items.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-white/5 p-10 text-center">
            <h2 className="text-2xl text-gray-100 font-bold">No Saved Items</h2>

            <p className="mt-3 text-slate-200">
              Save products while browsing and they'll appear here.
            </p>

            <Link
              to="/herkart/products"
              className="mt-6 inline-block rounded-xl bg-pink-600 px-6 py-3 font-semibold hover:bg-pink-500"
            >
              Browse Products
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-5">
              {items.map((item) => (
                <div
                  key={item._id}
                  className="rounded-3xl border border-white/10 bg-white/5 p-5"
                >
                  <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                    <div className="flex-1">
                      <h2 className="text-lg text-gray-100 font-bold">{item.name}</h2>

                      <div className="mt-2 flex flex-wrap gap-5 text-sm text-slate-200">
                        <span>
                          <FontAwesomeIcon
                            icon={faStore}
                            className="mr-2 text-pink-400"
                          />

                          {item.store}
                        </span>

                        <span>
                          <FontAwesomeIcon
                            icon={faMapMarkerAlt}
                            className="mr-2 text-pink-400"
                          />

                          {item.city}
                        </span>
                      </div>

                      <div className="mt-3 text-2xl font-bold text-pink-400">
                        ₹{item.price}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => decreaseQty(item._id)}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </button>

                      <div className="w-10 text-center font-bold">
                        {item.quantity}
                      </div>

                      <button
                        onClick={() => increaseQty(item._id)}
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-pink-600 hover:bg-pink-500"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>

                      <button
                        onClick={() => removeItem(item._id)}
                        className="ml-2 flex h-10 w-10 items-center justify-center rounded-xl bg-red-600 hover:bg-red-500"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-6">
              <div className="flex items-center justify-between">
                <span className="text-slate-400">Saved Items</span>

                <span className="font-bold">{totalItems}</span>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <span className="text-slate-400">Estimated Value</span>

                <span className="text-2xl font-bold text-pink-400">
                  ₹{totalValue}
                </span>
              </div>

              <div className="mt-6 flex gap-3">
                <Link
                  to="/herkart/products"
                  className="flex-1 rounded-xl border border-white/10 py-3 text-center font-semibold hover:bg-white"
                >
                  <FontAwesomeIcon icon={faShoppingBag} className="mr-2" />
                  Continue Shopping
                </Link>

                <Link
                    to="/herkart/checkout"
                    className="flex-1 rounded-xl border border-white/10 py-3 text-center font-semibold hover:bg-white"
                >
                    <FontAwesomeIcon icon={faShoppingCart} className="mr-2" />
                    Move All To Cart
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HerKartSaved;
